import { API_CONFIG } from '../config/api';

interface LivenessToken {
  access_key: string;
  secret_key: string;
  session_token: string;
  expiration: string;
}

interface LivenessSession {
  session_id: string;
}

interface LivenessVerificationResult {
  confidence: number;
  status: 'SUCCEEDED' | 'FAILED' | 'PROCESSING';
}

interface LivenessResult {
  confidence: number;
  status: 'SUCCEEDED' | 'FAILED' | 'PROCESSING';
}

interface VerifyResult {
  is_match: boolean;
  score: number;
  threshold: number;
}

interface SearchResult {
  is_match: boolean;
  score: number;
  threshold: number;
  user_id?: number;
}

export type ValidationType = 'LIVENESS' | 'VERIFY' | 'SEARCH';

export interface CombinedResult {
  liveness: LivenessResult;
  verify?: VerifyResult;
  search?: SearchResult;
}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

export const livenessService = {
  async getToken(): Promise<LivenessToken> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/liveness/token`,
        {
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get liveness token');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async createSession(): Promise<LivenessSession> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/liveness/session`,
        {
          method: 'POST',
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create liveness session');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async getSessionResult(sessionId: string): Promise<LivenessVerificationResult> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/liveness/sessions/${sessionId}`,
        {
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to verify liveness session');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async verifyWithUser(sessionId: string, userId: number): Promise<CombinedResult> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/liveness/sessions/${sessionId}/verify`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify({ user_id: userId }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to verify user');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  async searchUser(sessionId: string): Promise<CombinedResult> {
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/liveness/sessions/${sessionId}/search`,
        {
          method: 'POST',
          headers: getHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to search user');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  },
}; 